/**
 * Deferred Video Loader (Viewport Observer & Lazy Hydration)
 * Initial load: Only poster images are rendered.
 * Videos hydrate and play automatically once scrolled into viewport.
 */
(() => {
  'use strict';

  const isDesktop = () => window.innerWidth >= 990;

  function shouldHydrate(el) {
    const d = el.classList.contains('slideshow-video-desktop');
    const m = el.classList.contains('slideshow-video-mobile');
    return d ? isDesktop() : m ? !isDesktop() : true;
  }

  function hydrateVideo(wrapper) {
    if (!wrapper || wrapper.dataset.hydrated === 'true' || !shouldHydrate(wrapper)) return;
    const src = wrapper.dataset.videoSrc;
    if (!src) return;

    wrapper.dataset.hydrated = 'true';
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.autoplay = wrapper.dataset.autoplay !== 'false';
    video.loop = wrapper.dataset.loop !== 'false';
    video.muted = wrapper.dataset.muted !== 'false';
    video.playsInline = wrapper.dataset.playsinline !== 'false';
    if (wrapper.dataset.controls === 'true') video.controls = true;
    if (wrapper.dataset.poster) video.poster = wrapper.dataset.poster;

    const source = document.createElement('source');
    source.src = src;
    source.type = 'video/mp4';
    video.appendChild(source);

    const img = wrapper.querySelector('img');
    if (img) img.setAttribute('aria-hidden', 'true');

    wrapper.appendChild(video);
    video.load();
    if (video.autoplay) video.play().catch(() => { });
  }

  window.hydrateVideo = hydrateVideo;

  function observeVideos(root = document) {
    const targets = root.querySelectorAll('.js-deferred-video:not([data-hydrated="true"])');
    if (!targets.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const target = entry.target;
            hydrateVideo(target);
            observer.unobserve(target);
          });
        },
        { rootMargin: '50px 0px', threshold: 0.01 }
      );

      targets.forEach((el) => {
        if (shouldHydrate(el)) observer.observe(el);
      });
    } else {
      targets.forEach(hydrateVideo);
    }
  }

  function ensurePlaying(root = document) {
    const activeVideos = root.querySelectorAll('.swiper-slide-active .js-hero-video video, .slideshow-swiper__slide:first-child .js-hero-video video');
    activeVideos.forEach((video) => {
      if (video.paused && video.autoplay) {
        video.play().catch(() => {});
      }
    });
  }

  function init() {
    observeVideos();
    ensurePlaying();

    document.addEventListener(
      'slideshow:slidechange',
      (e) => {
        const active = e.target?.querySelector?.('.swiper-slide-active .js-deferred-video');
        if (active) hydrateVideo(active);
      },
      { passive: true }
    );

    document.addEventListener(
      'shopify:section:load',
      (e) => {
        observeVideos(e.target);
        ensurePlaying(e.target);
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

